/**
 * Upload size limits - ONE number for the whole app.
 *
 * Every feature used to carry its own literal (10MB in FileUploader, 50MB in
 * BaseForm, 100MB in the table wizard), so the same file was accepted on one
 * screen and refused on another, and none of them matched what the backend
 * actually allows. The backend caps a single uploaded file at 200MB; that is
 * the number here, and every uploader reads it.
 *
 * This is the per-FILE ceiling, not a per-request one. Requests are kept small
 * by batching (`services/uploads/uploadBatching`) - a rule about the aggregate,
 * decided separately, because the two limits exist for different reasons:
 * this one is storage policy, that one is what the EU edge will carry.
 */

/** Largest single file the backend accepts. */
export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

/** Human-readable size, for the message shown when a file is refused. */
export function formatUploadBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** The label the cap is described by in copy - "200 MB". */
export const MAX_UPLOAD_LABEL = formatUploadBytes(MAX_UPLOAD_BYTES);

export interface OversizedSplit {
  /** Files within the cap - safe to hand to the batcher. */
  accepted: File[];
  /** Files above the cap, with the reason to show the user. */
  oversized: Array<{ file: string; file_name: string; reason: string }>;
}

/**
 * Split a picked set into what can be uploaded and what is simply too big.
 *
 * Batching bounds the aggregate of a request, never an individual file - a
 * single 300MB PDF is one request no matter how it is grouped. Refusing it
 * here is the difference between a sentence the user can act on and a raw
 * `ERR_HTTP2_PROTOCOL_ERROR` from the edge.
 */
export function partitionOversized(
  files: File[],
  maxBytes: number = MAX_UPLOAD_BYTES,
): OversizedSplit {
  const accepted: File[] = [];
  const oversized: OversizedSplit['oversized'] = [];
  files.forEach((file) => {
    if (file.size > maxBytes) {
      const reason = `Too large (${formatUploadBytes(file.size)}). The limit is ${formatUploadBytes(maxBytes)} per file.`;
      oversized.push({ file: file.name, file_name: file.name, reason });
    } else {
      accepted.push(file);
    }
  });
  return { accepted, oversized };
}

/** One sentence naming the files that were refused for being too big. */
export function oversizedMessage(oversized: OversizedSplit['oversized']): string {
  if (!oversized.length) return '';
  const names = oversized.map((o) => o.file_name).join(', ');
  return oversized.length === 1
    ? `${names} is larger than ${MAX_UPLOAD_LABEL} and was not uploaded.`
    : `${oversized.length} files are larger than ${MAX_UPLOAD_LABEL} and were not uploaded: ${names}`;
}
